// Operating Hours Business Logic
export const OPERATING_HOURS = {
  // Monday-Thursday: 17:00-20:00 (last seating 20:00, close 20:30)
  1: [{ start: '17:00', end: '20:00', lastSeating: '20:00' }],
  2: [{ start: '17:00', end: '20:00', lastSeating: '20:00' }],
  3: [{ start: '17:00', end: '20:00', lastSeating: '20:00' }],
  4: [{ start: '17:00', end: '20:00', lastSeating: '20:00' }],
  
  // Friday: 17:00-21:00 (last seating 21:00, close 21:30)
  5: [{ start: '17:00', end: '21:00', lastSeating: '21:00' }],
  
  // Saturday: 11:00-14:00 (Lunch) AND 16:00-21:00 (Dinner)
  6: [
    { start: '11:00', end: '14:00', lastSeating: '14:00', label: 'Lunch' },
    { start: '16:00', end: '21:00', lastSeating: '21:00', label: 'Dinner' }
  ],
  
  // Sunday: 11:00-14:00 (Lunch) AND 16:00-20:00 (Dinner)
  0: [
    { start: '11:00', end: '14:00', lastSeating: '14:00', label: 'Lunch' },
    { start: '16:00', end: '20:00', lastSeating: '20:00', label: 'Dinner' }
  ]
};

/**
 * Validate if a time is within operating hours for a given date
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} time - Time in HH:MM format
 * @returns {object} { valid: boolean, message: string, lastSeating: string }
 */
export const validateOperatingHours = (dateStr, time) => {
  const date = new Date(dateStr + 'T12:00:00');
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  const hours = OPERATING_HOURS[dayOfWeek];
  
  if (!hours || hours.length === 0) {
    return {
      valid: false,
      message: 'We are closed on this day',
      lastSeating: null
    };
  }

  // Check if time falls within any of the operating periods
  for (const period of hours) {
    if (isTimeInRange(time, period.start, period.end)) {
      return {
        valid: true,
        message: 'Time is available',
        lastSeating: period.lastSeating,
        period: period.label || 'Dinner'
      };
    }
  }

  // Time is outside operating hours - find the last seating time
  const lastPeriod = hours[hours.length - 1];
  const periodLabel = lastPeriod.label ? `${lastPeriod.label} ` : '';
  
  return {
    valid: false,
    message: `Sorry, we are closed. Last ${periodLabel}seating is at ${lastPeriod.lastSeating}`,
    lastSeating: lastPeriod.lastSeating
  };
};

/**
 * Check if a time is within a range
 * @param {string} time - Time to check (HH:MM)
 * @param {string} start - Start time (HH:MM)
 * @param {string} end - End time (HH:MM)
 * @returns {boolean}
 */
const isTimeInRange = (time, start, end) => {
  const timeMinutes = timeToMinutes(time);
  const startMinutes = timeToMinutes(start);
  const endMinutes = timeToMinutes(end);
  
  return timeMinutes >= startMinutes && timeMinutes <= endMinutes;
};

/**
 * Convert HH:MM to minutes since midnight
 * @param {string} time - Time in HH:MM format
 * @returns {number} Minutes since midnight
 */
const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Get available time slots for a given date
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {Array<string>} Array of time slots in HH:MM format
 */
export const getAvailableTimeSlots = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00');
  const dayOfWeek = date.getDay();
  
  const hours = OPERATING_HOURS[dayOfWeek];
  
  if (!hours || hours.length === 0) {
    return [];
  }

  const slots = [];
  
  for (const period of hours) {
    const startMinutes = timeToMinutes(period.start);
    const endMinutes = timeToMinutes(period.lastSeating);
    
    // Generate slots every 30 minutes
    for (let minutes = startMinutes; minutes <= endMinutes; minutes += 30) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      slots.push(`${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`);
    }
  }
  
  return slots;
};

/**
 * Get day name
 * @param {number} dayOfWeek - 0 = Sunday, 1 = Monday, etc.
 * @returns {string}
 */
export const getDayName = (dayOfWeek) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayOfWeek];
};
